// components/Footer.js (Redesigned)
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faChevronRight,
  faArrowUp
} from '@fortawesome/free-solid-svg-icons';
import { 
  faFacebook, 
  faInstagram, 
  faTiktok
} from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import logo from '../assets/logo.png';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const socialLinks = [
    { icon: faFacebook, url: '#', label: 'Facebook' },
    { icon: faTiktok, url: '#', label: 'TikTok' },
    { icon: faInstagram, url: '#', label: 'Instagram' },
  ];

  return (
    <footer className="relative bg-stone-950 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
      </div>

      {/* Main Footer Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
          
          {/* Company Info */}
          <div className="px-2 sm:px-0">
            <Link to="/" className="flex items-center mb-6 group">
              <div className="relative mr-3">
                {/* Logo Image */}
                <img 
                  src={logo} 
                  alt="KA Construction Solutions LTD" 
                  className="h-12 lg:h-14 w-auto brightness-0 invert"
                />
                {/* Badge overlay */}
                <div className="absolute -top-2 -right-2 w-5 h-5 lg:w-6 lg:h-6 bg-emerald-500 rounded-full border-2 border-stone-950 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">KA</span>
                </div>
              </div>
              <div>
                <span className="text-xl lg:text-2xl font-bold text-white group-hover:text-yellow-400 transition-colors">
                  KA Construction 
                </span>
                <div className="text-blue-300 text-xs font-bold lg:text-sm group-hover:text-blue-200 transition-colors">Solutions LTD</div>
              </div>
            </Link>
            
            <p className="text-gray-300 mb-6 lg:mb-8 leading-relaxed text-sm font-bold lg:text-base">
              We transform visions into reality with unparalleled craftsmanship, 
              innovative design, and sustainable practices. Your dream home is our passion.
            </p>
            
            <div className="flex space-x-2 lg:space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  aria-label={social.label}
                  className="w-8 h-8 lg:w-10 lg:h-10 bg-gray-800 hover:bg-gradient-to-br hover:from-amber-600 hover:to-orange-600 rounded-lg flex items-center justify-center text-gray-300 hover:text-white transition-all duration-300 transform hover:-translate-y-1"
                >
                  <FontAwesomeIcon icon={social.icon} className="h-4 w-4 lg:h-5 lg:w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="px-2 sm:px-0">
            <h3 className="text-lg lg:text-xl font-bold text-white mb-6 lg:mb-8 relative inline-block">
              Quick Links
              <div className="absolute -bottom-2 left-0 w-10 lg:w-12 h-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"></div>
            </h3>
            <ul className="space-y-2 lg:space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.href}
                    className="text-gray-300 font-bold hover:text-white flex items-center transition-all duration-300 group text-sm lg:text-base"
                  >
                    <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 mr-2 lg:mr-3 text-yellow-500 group-hover:translate-x-1 transition-transform" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="px-2 sm:px-0">
            <h3 className="text-lg lg:text-xl font-bold text-white mb-6 lg:mb-8 relative inline-block">
              Contact Us
              <div className="absolute -bottom-2 left-0 w-10 lg:w-12 h-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"></div>
            </h3>
            <div className="space-y-4 lg:space-y-6">
              <a 
                href="https://maps.google.com/?q=19+Fenham+Road+Newcastle+Upon+Tyne+NE4+5PB"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start group"
              >
                <div className="bg-gray-800 p-2 lg:p-3 rounded-lg mr-3 lg:mr-4 group-hover:bg-blue-900 transition-colors flex-shrink-0">
                  <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 lg:h-5 lg:w-5 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm lg:text-base group-hover:text-blue-300 transition-colors">Visit Our Office</div>
                  <div className="text-gray-300 text-xs lg:text-sm font-bold group-hover:text-blue-200 transition-colors break-words">19 Fenham Road Newcastle Upon Tyne NE4 5PB</div>
                </div>
              </a>
              
              <a 
                href="tel:07796592487"
                className="flex items-center group"
              >
                <div className="bg-gray-800 p-2 lg:p-3 rounded-lg mr-3 lg:mr-4 group-hover:bg-emerald-900 transition-colors flex-shrink-0">
                  <FontAwesomeIcon icon={faPhone} className="h-4 w-4 lg:h-5 lg:w-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-white font-semibold text-sm lg:text-base group-hover:text-emerald-300 transition-colors">Call Us Now</div>
                  <div className="text-gray-300 text-sm font-bold lg:text-base group-hover:text-emerald-200 transition-colors">07796 592 487</div>
                </div>
              </a>
              
              <a 
                href="mailto:kaconstructionsolutions@gmail.com"
                className="flex items-center group"
              >
                <div className="bg-gray-800 p-2 lg:p-3 rounded-lg mr-3 lg:mr-4 group-hover:bg-amber-900 transition-colors flex-shrink-0">
                  <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 lg:h-5 lg:w-5 text-amber-400" />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm lg:text-base group-hover:text-amber-300 transition-colors">Email Us</div>
                  <div className="text-gray-300 text-xs lg:text-sm font-bold group-hover:text-amber-200 transition-colors break-words">Kaconstructionsolutions@gmail.com</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 lg:mt-12 pt-6 lg:pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-center items-center px-2 sm:px-0">
            <div className="text-white text-xl lg:text-sm text-center font-bold mb-4 md:mb-0">
              © {new Date().getFullYear()} KA Construction Solutions LTD. All rights reserved.
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-600 to-amber-600 text-white rounded-full flex items-center justify-center shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 z-40"
        aria-label="Back to top"
      >
        <FontAwesomeIcon icon={faArrowUp} className="h-4 w-4 sm:h-5 sm:w-5" />
      </button>
    </footer>
  );
};

export default Footer;